import { EventEmitter, Optional, Self } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl
} from '@angular/forms';

import { getFormErrorMessage } from '@app/core/ui/helpers/get-form-error-message';

export interface SelectProps {
  name: string;
  label: string;
  disabled: boolean;
  rounded: 'sm' | 'md' | 'lg';
  size: 'sm' | 'md' | 'lg';
}

export abstract class BaseSelect<T> implements ControlValueAccessor {
  private _value: T[keyof T] | T[keyof T][];
  get value(): T[keyof T] | T[keyof T][] {
    return this._value;
  }
  set value(v: T[keyof T] | T[keyof T][]) {
    if (v !== this.value) {
      this._value = v;
      this.onChangeCb(v);
    }
  }

  protected onchange = new EventEmitter<any>();

  protected _name: SelectProps['name'];
  protected _label: SelectProps['label'];
  protected _disabled: SelectProps['disabled'];
  protected _rounded: SelectProps['rounded'] = 'md';
  protected _size: SelectProps['rounded'] = 'md';

  get selectProps(): SelectProps {
    return {
      name: this._name,
      label: this._label,
      disabled: this._disabled,
      rounded: this._rounded,
      size: this._size
    };
  }

  get selectLabel(): string {
    return this.selectProps.label;
  }

  get isDisabled(): boolean {
    return this.selectProps.disabled;
  }

  get id(): Readonly<string> {
    return this.selectProps.label?.toLowerCase().replace(' ', '-');
  }

  constructor(@Self() @Optional() private readonly _ngControl: NgControl) {
    if (this._ngControl !== null) {
      this._ngControl.valueAccessor = this;
    }
  }

  get isRequired(): boolean {
    if (!this._ngControl?.control.validator) {
      return false;
    }
    const validator = this._ngControl.control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  get isInvalid(): boolean {
    if (!this._ngControl?.control.validator) {
      return false;
    }
    const { dirty, touched } = this._ngControl;
    return this._ngControl
      ? (dirty || touched) && this._ngControl.invalid
      : false;
  }

  get isValid(): boolean {
    if (!this._ngControl?.control.validator) {
      return false;
    }
    const { dirty, touched } = this._ngControl;
    return this._ngControl
      ? (dirty || touched) && this._ngControl.valid
      : false;
  }

  get error(): string {
    if (this.isValid || !this._ngControl || !this.selectProps.name) {
      return null;
    }

    for (const propertyName in this._ngControl.errors) {
      if (
        this._ngControl.errors.hasOwnProperty.bind(propertyName) &&
        this._ngControl.control.touched
      ) {
        const propertyValue: Record<string, string> =
          this._ngControl.errors[propertyName];

        return getFormErrorMessage(
          this.selectProps.name,
          propertyName,
          propertyValue
        );
      }
    }
    return null;
  }

  get roundedClass(): Record<string, boolean> {
    const { rounded } = this.selectProps;

    return {
      'rounded-sm': rounded === 'sm',
      'rounded-md': rounded === 'md',
      'rounded-lg': rounded === 'lg'
    };
  }

  get sizeClass(): Record<string, boolean> {
    const { size } = this.selectProps;

    return {
      'min-h-8': size === 'sm',
      'min-h-9': size === 'md',
      'min-h-10': size === 'lg'
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChangeCb: (_: T[keyof T] | T[keyof T][]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouchedCb: (_: T[keyof T] | T[keyof T][]) => void = () => {};

  writeValue(v: T[keyof T] | T[keyof T][]): void {
    this.value = v;
  }

  registerOnChange(fn: (_: T[keyof T] | T[keyof T][]) => void): void {
    this.onChangeCb = fn;
  }

  registerOnTouched(fn: (_: T[keyof T] | T[keyof T][]) => void): void {
    this.onTouchedCb = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }
}
